/* eslint-disable react/no-danger */
import { useTranslation } from '@starsoft/common/hooks';
import { TransactionsCommonCoppyAddressProps } from './props';
import { Input } from '@starsoft/common/components/Input';
import styles from './styles.module.scss';
import { Button } from '@starsoft/common/components/Button';

/**
 * TransactionsCommonCopyAddress Component
 * This component is responsible for displaying an address and providing a button to copy it.
 * It shows a loading input if the data is still loading.
 *
 * @param {TransactionsCommonCoppyAddressProps} props - The properties for the component.
 * @param {string} props.address - The address to be displayed and copied.
 * @param {() => void} props.onCopy - Callback function to handle the copy action.
 * @param {boolean} props.isLoading - Indicates if the data is still loading.
 *
 * @returns {JSX.Element} The rendered component displaying the address and a copy button, or a loading input if isLoading is true.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonCopyAddress
 *   address="1234 Main St, Anytown, USA"
 *   onCopy={() => function('Address copied')}
 *   isLoading={false}
 * />
 */
export default function TransactionsCommonCopyAddress({
  address,
  onCopy,
  isLoading,
}: TransactionsCommonCoppyAddressProps) {
  const { t } = useTranslation('deposit');

  // If the component is in a loading state, display a loading input
  if (isLoading) {
    return <Input isLoading isContainerColor />;
  }

  // Otherwise, display the address and a button to copy it
  return (
    <div className={styles.address}>
      <div
        className={styles.address__wrapper}
        dangerouslySetInnerHTML={{
          __html: `<p>${address}</p>`,
        }}
      />
      <Button
        isSecondary
        isSmall
        onClick={onCopy}
        className={styles.address__button}
      >
        <i className="fa-solid fa-copy" />
        {t('common:copy_label')}
      </Button>
    </div>
  );
}
